import { match } from 'ts-pattern';
import type { ParsedEntity, ParsedMethod, ParsedPackage } from '@interxfi/j5-parse/types/parsed';
import { applyDisplayNameCase, buildSafeIdForSection } from '../../utils';

function buildPathForItem(path: string, hash?: string): string {
  const url = new URL(path, window.location.origin);
  if (hash) {
    url.hash = hash;
  }

  url.search = window.location.search;

  return url.toString().replace(window.location.origin, '');
}

export interface ApiReferencePackageStructureIntroduction {
  kind: 'introduction';
  value: {
    anchor: string;
    fullPath: string;
    content: string | undefined;
    title: string;
  };
}

export interface ApiReferencePackageStructureMethod {
  kind: 'method';
  value: {
    anchor: string;
    fullPath: string;
    method: ParsedMethod;
    title: string;
  };
}

export interface ApiReferencePackageStructureEntity {
  kind: 'entity';
  value: {
    anchor: string;
    content?: string;
    entityName: string;
    entity: ParsedEntity;
    eventsAndStatusAnchor: string;
    eventsAndStatusPath: string;
    fullPath: string;
    title: string;
    methods: ApiReferencePackageStructureMethod[];
  };
}

export type ApiReferencePackageStructureItem =
  | ApiReferencePackageStructureIntroduction
  | ApiReferencePackageStructureEntity
  | ApiReferencePackageStructureMethod;

function mapMethodToStructureItem(method: ParsedMethod, entityName?: string): ApiReferencePackageStructureMethod {
  const anchor = [entityName, buildSafeIdForSection(method.name)].filter(Boolean).join('.');

  return {
    kind: 'method',
    value: {
      anchor,
      fullPath: buildPathForItem(`/docs/api-reference/${method.parentService.parentPackage.name}`, anchor),
      method,
      title: applyDisplayNameCase(method.name),
    },
  };
}

const INTRO_HASH = 'intro';

export function getApiReferenceNavigationStructureForPackage(pkg: ParsedPackage): ApiReferencePackageStructureItem[] {
  const structure: ApiReferencePackageStructureItem[] = [
    {
      kind: 'introduction',
      value: {
        anchor: INTRO_HASH,
        fullPath: buildPathForItem(`/docs/api-reference/${pkg.name}`, INTRO_HASH),
        content: pkg.introduction,
        title: pkg.label || pkg.name,
      },
    },
  ];
  const entityByName = new Map<string, ParsedEntity>();
  const methodsByEntity = new Map<string, ParsedMethod[]>();
  const nonEntityMethods: ParsedMethod[] = [];

  for (const service of pkg.services || []) {
    for (const method of service.methods || []) {
      const entityName = match(method.methodType)
        .with({ '!type': 'stateCommand' }, (e) => e.stateCommand.entityName)
        .with({ '!type': 'stateQuery' }, (e) => e.stateQuery.entityName)
        .otherwise(() => undefined);

      if (entityName) {
        if (!entityByName.has(entityName) && method.relatedEntity) {
          entityByName.set(entityName, method.relatedEntity);
        }

        if (!methodsByEntity.has(entityName)) {
          methodsByEntity.set(entityName, []);
        }

        methodsByEntity.get(entityName)?.push(method);
      } else {
        nonEntityMethods.push(method);
      }
    }
  }

  if (methodsByEntity.size || nonEntityMethods.length) {
    for (const [entityName, entityMethods] of methodsByEntity.entries()) {
      const titleCasedEntityName = applyDisplayNameCase(entityName);
      const entityAnchor = buildSafeIdForSection(`${applyDisplayNameCase(pkg.label ?? pkg.name)}_${titleCasedEntityName}`);
      const eventsAndStatusAnchor = `${entityAnchor}_EventsAndStatus`;
      const entitySchema = entityByName.get(entityName);
      const entityPath = buildPathForItem(`/docs/api-reference/${pkg.name}`, entityAnchor);
      const eventsAndStatusPath = buildPathForItem(`/docs/api-reference/${pkg.name}`, eventsAndStatusAnchor);

      structure.push({
        kind: 'entity',
        value: {
          anchor: entityAnchor,
          content: entitySchema?.overview,
          entityName: titleCasedEntityName,
          entity: entitySchema || ({ entity: entityName } as ParsedEntity),
          eventsAndStatusAnchor,
          eventsAndStatusPath,
          fullPath: entityPath,
          title: titleCasedEntityName,
          methods: entityMethods.map((method) => mapMethodToStructureItem(method, entityAnchor)),
        },
      });
    }

    for (const method of nonEntityMethods) {
      structure.push(mapMethodToStructureItem(method));
    }
  }

  return structure;
}
